const CACHE_PREFIX = 'lotto-club:lottery-data:v2:';
export const LOTTERY_CACHE_TTL_MS = 60_000;

const memoryCache = new Map();
const inFlightRequests = new Map();
let summarySnapshot = null;
let summarySnapshotAt = 0;
let summaryRequest = null;

function cacheKey(game) {
  return `${CACHE_PREFIX}${game}`;
}

function getStorage() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function validCacheEntry(value) {
  return Boolean(
    value &&
    typeof value === 'object' &&
    Number.isFinite(value.savedAt) &&
    value.data &&
    typeof value.data === 'object'
  );
}

export function readLotteryCache(game) {
  const inMemory = memoryCache.get(game);
  if (validCacheEntry(inMemory)) {
    return inMemory;
  }

  const storage = getStorage();
  if (!storage) {
    return null;
  }

  try {
    const parsed = JSON.parse(storage.getItem(cacheKey(game)));
    if (!validCacheEntry(parsed)) {
      return null;
    }
    memoryCache.set(game, parsed);
    return parsed;
  } catch {
    storage.removeItem(cacheKey(game));
    return null;
  }
}

function writeLotteryCache(game, data) {
  const entry = { savedAt: Date.now(), data };
  memoryCache.set(game, entry);

  const storage = getStorage();
  if (storage) {
    try {
      storage.setItem(cacheKey(game), JSON.stringify(entry));
    } catch {
      // The in-memory cache still works when browser storage is unavailable/full.
    }
  }

  window.dispatchEvent(
    new CustomEvent('lottery-data-updated', { detail: { game, data } })
  );
  return data;
}

async function fetchJson(url) {
  const response = await fetch(url, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin'
  });
  if (!response.ok) {
    throw new Error(`Lottery data request failed (${response.status})`);
  }
  return response.json();
}

function fetchSummary() {
  const now = Date.now();
  if (summarySnapshot && now - summarySnapshotAt < 1_000) {
    return Promise.resolve(summarySnapshot);
  }
  if (summaryRequest) {
    return summaryRequest;
  }

  summaryRequest = fetchJson('/api/summary')
    .then((summary) => {
      summarySnapshot = summary;
      summarySnapshotAt = Date.now();
      return summary;
    })
    .finally(() => {
      summaryRequest = null;
    });
  return summaryRequest;
}

export function shapeLotteryData(game, summary, draws) {
  const nextDraw = summary.upcomingDrawings.find(
    (drawing) => drawing.game === game
  );
  const currentJackpot = summary.currentJackpots?.find(
    (jackpot) => jackpot.game === game
  );
  const previousDraws = draws.rows.map((draw) => ({
    // Noon UTC keeps a date-only result on the same US calendar day.
    DrawDate: `${draw.draw_date}T12:00:00.000Z`,
    WinningNumbers: [...draw.main_numbers, draw.bonus_number].map((number) => ({
      Number: String(number).padStart(2, '0')
    }))
  }));

  return {
    DrawGameId: game,
    PreviousDraws: previousDraws,
    JackpotHistory: (summary.jackpotHistory ?? [])
      .filter((snapshot) => snapshot.game === game)
      .map((snapshot) => ({
        estimatedJackpot: snapshot.estimatedJackpot,
        cashValue: snapshot.cashValue,
        nextDrawAt: snapshot.nextDrawAt,
        sourceUpdatedAt: snapshot.sourceUpdatedAt,
        fetchedAt: snapshot.fetchedAt
      })),
    NextDraw: {
      DrawDate: nextDraw?.drawAt ?? null,
      JackpotAmount: currentJackpot?.estimatedJackpot ?? null,
      EstimatedCashValue: currentJackpot?.cashValue ?? null,
      EstimatedFederalTax: currentJackpot?.estimatedFederalTax ?? null,
      EstimatedCaliforniaTakeHome:
        currentJackpot?.estimatedCaliforniaTakeHome ?? null,
      TaxAssumptions: currentJackpot?.taxAssumptions ?? null
    }
  };
}

export function loadLotteryData(game) {
  const cached = readLotteryCache(game);
  if (cached && Date.now() - cached.savedAt < LOTTERY_CACHE_TTL_MS) {
    return Promise.resolve(cached.data);
  }

  const existingRequest = inFlightRequests.get(game);
  if (existingRequest) {
    return existingRequest;
  }

  const request = Promise.all([
    fetchSummary(),
    fetchJson(`/api/draws?game=${game}&limit=6`)
  ])
    .then(([summary, draws]) => writeLotteryCache(
      game,
      shapeLotteryData(game, summary, draws)
    ))
    .finally(() => {
      inFlightRequests.delete(game);
    });

  inFlightRequests.set(game, request);
  return request;
}

export function warmLotteryCache(games) {
  return Promise.allSettled(games.map((game) => loadLotteryData(game)));
}

export function clearLotteryCache() {
  memoryCache.clear();
  inFlightRequests.clear();
  summarySnapshot = null;
  summarySnapshotAt = 0;
  summaryRequest = null;

  const storage = getStorage();
  if (!storage) {
    return;
  }
  for (let index = storage.length - 1; index >= 0; index -= 1) {
    const key = storage.key(index);
    if (key?.startsWith(CACHE_PREFIX)) {
      storage.removeItem(key);
    }
  }
}
